import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight, Smartphone, Divide } from "lucide-react";
import { LanguageSelectorCompact } from "@/components/LanguageSelector";
import BankIDLogin from "@/components/BankIDLogin";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showBankID, setShowBankID] = useState(false);
  const { login, setToken } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success(t('auth.loginSuccess'));
      navigate("/");
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || t('auth.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  const handleBankIDSuccess = (token) => {
    if (setToken) {
      setToken(token);
    }
    toast.success(t('auth.loginSuccess'));
    navigate("/");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 p-4">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4">
        <LanguageSelectorCompact />
      </div>

      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/yubi-logo.png" 
            alt="YUBI AI - Beyond Basic Bookkeeping" 
            className="h-44 w-auto mx-auto object-contain"
          />
        </div>

        {showBankID ? (
          <BankIDLogin 
            onBack={() => setShowBankID(false)}
            onSuccess={handleBankIDSuccess}
          />
        ) : (
          <Card className="border-slate-700 bg-slate-800/50 backdrop-blur shadow-2xl" data-testid="login-card">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl text-white">{t('auth.login')}</CardTitle>
              <CardDescription className="text-slate-400">
                {t('auth.noAccount')?.replace('?', '') || 'Ange dina uppgifter for att fortsatta'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">{t('auth.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                    <Input
                      id="email"
                      type="email"
                      placeholder="namn@foretag.se"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-indigo-500"
                      required
                      data-testid="login-email"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-300">{t('auth.password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                    <Input
                      id="password"
                      type="password"
                      placeholder={t('auth.password')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-indigo-500"
                      required
                      data-testid="login-password"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t('common.loading')}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      {t('auth.login')}
                      <ArrowRight size={16} />
                    </span>
                  )}
                </Button>
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-600" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-slate-800 px-2 text-slate-400">eller</span>
                </div>
              </div>

              {/* BankID Button */}
              <Button
                type="button"
                variant="outline"
                className="w-full border-slate-600 text-slate-300 hover:bg-slate-700/50 hover:text-white"
                onClick={() => setShowBankID(true)}
                data-testid="bankid-login-btn"
              >
                <Smartphone className="mr-2" size={18} />
                Logga in med BankID
              </Button>

              <div className="mt-6 text-center text-sm">
                <span className="text-slate-400">{t('auth.noAccount')} </span>
                <Link to="/register" className="text-purple-400 hover:text-purple-300 font-medium" data-testid="register-link">
                  {t('auth.createAccount')}
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Login;
